import React from 'react';
import { useState,  useRef,useEffect, useLayoutEffect } from 'react';
import './classes/CourseOverview.css';
import './classes/marquee.css';
import './classes/ConferenceRegistration.css';
import BeTheFirstToKnowPopup from './BeTheFirstToKnowPopup';
import DocTalkPopup from './DocTalkPopup';
import CurrencyConverter from "./shortcodes/CurrencyConverter";
import CourseCalendar from "./shortcodes/CourseCalendar";
import ReferralRegistrationForm, { REFERRAL_FORM_PAGES } from './ReferralRegistrationForm';
import SidebarQuickForm from './SidebarQuickForm';
import Mentor from './Mentor';
import { setUpTalkWithLynnForm } from './TalkWithLynnForm';
import { setUpAdvancedCertificateLeadForm } from './AdvancedCertificateLeadForm';
import { createRoot } from "react-dom/client";

import { configData } from "../config";
import drupalAutop from "./drupalAutop";
import { keepLiveLinksOnSite } from "./liveSiteLinks";
import { addYoutubePlayers } from "./youtubePlayers";

// Pages whose text format on the live site has Drupal's "Convert line breaks into HTML" filter:
// there a double <br /> in a paragraph starts a new paragraph (drupalAutop). The format is not
// copied to this site, so the pages are listed by node id: the Master's of Professional Studies
// degree page (274987), and the basic pages whose live paragraphs and line breaks come out of their
// stored bodies that way (checked for every basic page with no sidebars on 2026-10-26: Staff, Logos,
// the directors pages, the career advisors, the recaps and articles, ...)
const LINE_BREAK_PAGES = new Set([
    274987,
    67, 68, 77, 78, 79, 81, 587, 614, 616, 732, 788, 794, 816, 1109, 1110, 1112, 1113, 3354, 3434, 3435,
    3436, 3441, 3443, 3444, 3623, 3704, 210455, 516309, 516312, 516316,
]);

// Audio players in page bodies, by the name of the Drupal script that sets each one up
// (/sites/all/themes/smww/js/audio/<name>): the picture and recording it plays, and the title
// it shows (HTML; none: the body's own)
const AUDIO_PLAYERS = {
    'mel-kipper.js': { image: '/audio/mel_kiper.png', audio: '/audio/MelKiperJrAudio.mp3' },
    // The baseball player development course (the basketball, hockey and soccer ones load the
    // script too, but have no player)
    'bpd_audio.js': { image: '/audio/wispod.png', audio: '/audio/dan_duquette_BPD.mp3' },
    // The motor sports management course
    'larry-mc.js': { image: '/audio/larry-mcre.png', audio: '/audio/LarryMcReynoldsAudio.mp3' },
    // Loaded by the football player development and sports sales and marketing courses, whose
    // players are commented out or gone
    'course_all.js': { image: '/audio/mel_kiper_jr.jpg', audio: '/sites/default/files/media/Mel_Kiper_Jr_Audio.mp3' },
    'greghylton_audio.js': { image: '/audio/wispod.png', audio: '/audio/greg_hylton.mp3' },
    // The NFL Combine conference
    'all.js': {
        image: '/audio/audio_icon_lynn.png',
        audio: '/audio/nfl_audio.mp3',
        name: 'Business of Football Podcast <br/><span class="music_author_name"> Dr. Lynn Lashbrook</span>',
    },
};

// A course body saved from the old page editor begins with its Drupal PHP block broken in two at
// the block's first ">" (in "$UTCdate->"): the start is still PHP but closed there ("<?php ...
// $UTCdate-?>"), and the rest is text up to "?&gt;", on its own or in a paragraph of its own (the
// basketball, football scout and soccer apprenticeships). The server takes the PHP half out of
// the page (so a body here may begin with the text half only); the edit form gets both. The live
// site never shows either, so both are taken out.
export const stripLeftoverPhp = (body) =>
    String(body || '').replace(/^\s*(?:<\?php(?:(?!\?>)[\s\S])*[-=]\?>\s*)?(?:<p>)?[^<]*\?&gt;\s*(?:<\/p>)?\s*/, '');

// A link to one of the live theme's font stylesheets (Lato, Open Sans Extrabold, Open Sans Bold;
// the salary pages link all three; the course testimonial pages' and the FAQ pages' button
// stylesheets, with their Inter Bold font; and the audio players' Font Awesome), with or without
// the live domain: the href= with its quote, then the stylesheet's name. Browsers load a font from
// another site only when that site allows it, and neither the Drupal site nor the live site does,
// so this site serves copies of them and their fonts (public/fonts).
const THEME_FONT_STYLESHEET = /(\shref=["'])(?:https?:\/\/(?:www\.)?sportsmanagementworldwide\.com)?\/sites\/all\/themes\/smww\/(?:css\/(latofonts|testimonial_button|faq_button|audio\/font-awesome)|font\/(open_sans_(?:extra)?bold))\.css(?=["'?#])/gi;
const themeFontStylesheet = (link, attribute, css, font) => attribute + process.env.PUBLIC_URL + '/fonts/' + (css || font).toLowerCase() + '.css';

// The video players' stylesheet the Conferences page links on the live domain, over plain http
// (/scripts/video_style.css), which a browser does not load into an https page: this site's own
// copy instead (public/scripts, the live code's video_style.css). Live's address answers 404, so no
// styles reach its page either, and the copy's selectors (#player, .youtube) match nothing there.
const VIDEO_STYLESHEET = /(\shref=["'])(?:https?:)?\/\/(?:www\.)?sportsmanagementworldwide\.com(\/scripts\/video_style\.css)(?=["'?#])/gi;

// Opens or closes a collapsible section with a slide, as jQuery's slideToggle did on the Drupal
// site (400ms). A click during a slide takes over from it. The site's jQuery (1.7.2) leaves an
// open section's overflow hidden, which keeps the margin of its first heading inside it (25px).
let slides = 0;
const slideSection = (section, opening) => {
    const slide = String(++slides);
    section.dataset.slide = slide;
    section.dataset.open = String(opening);
    section.getAnimations().forEach((animation) => animation.cancel());
    const done = () => {
        if (section.dataset.slide !== slide) return;
        section.getAnimations().forEach((animation) => animation.cancel());
        section.style.display = opening ? 'block' : 'none';
    };
    section.style.display = 'block';
    section.style.overflow = 'hidden';
    const height = section.scrollHeight + 'px';
    section.animate({ height: opening ? ['0px', height] : [height, '0px'] }, { duration: 400, easing: 'ease' }).onfinish = done;
    // A tab in the background does not run animations: the section still ends up open or closed
    setTimeout(done, 450);
};

// What a page's own inline script does with its older collapsible sections:
// $('#morebar2').click(function(){ $('#morebar22').slideToggle(); }) and $('.morebarinfo').hide()
const CLICK_SLIDE_TOGGLE = /\$\(\s*(['"])(.+?)\1\s*\)\.click\(\s*function\s*\(\s*\)\s*\{\s*\$\(\s*(['"])(.+?)\3\s*\)\.slideToggle\(\s*\)/g;
const HIDE = /\$\(\s*(['"])(.+?)\1\s*\)\.hide\(\s*\)/g;
// Selectors from a page's script, which the browser may not accept: none of those match
const matches = (el, selector) => {
    try {
        return el.matches(selector);
    } catch (e) {
        return false;
    }
};
const within = (el, selector) => {
    try {
        return !!el.closest(selector);
    } catch (e) {
        return false;
    }
};
const select = (root, selector) => {
    try {
        return [...root.querySelectorAll(selector)];
    } catch (e) {
        return [];
    }
};

// editorClasses: style Drupal's editor classes (rteleft, rtecenter, ...) as the live site does
// for this page and visitor (Page.js)
const Body = ({ page, editorClasses = true }) => {
  const app_url = configData.APP_URL;
 const bodyRef = useRef(null);
 const [showBeTheFirstToKnow, setShowBeTheFirstToKnow] = useState(false);
 // The DOC Talks form that is open: { mode: 'register' | 'recording', id, mentors }
 const [docTalkPopup, setDocTalkPopup] = useState(null);
useEffect(() => {
    const ul = document.getElementById("marquee-horizontal");

    if (ul && !ul.dataset.initialized) {
        ul.innerHTML += ul.innerHTML;
        ul.dataset.initialized = "true";
    }
}, [page?.body]);
useEffect(() => {
    if (!bodyRef.current) return;

    bodyRef.current
        .querySelectorAll('[data-type="currency-converter"]')
        .forEach((element) => {
            // Mount it once: a second converter on the same element would add a second price line
            if (element.dataset.initialized) return;
            element.dataset.initialized = "true";

            createRoot(element).render(
                <CurrencyConverter
                    price={parseFloat(element.dataset.price)}
                />
            );
        });

         bodyRef.current
        .querySelectorAll('[data-type="mobile-quick-form"]')
        .forEach((element) => {
            // Live fills its [mobile_quick_form] token with the course's tokens, so on a basic page
            // (Your Path to Success as a Football Coach, live's course_page node) it prints nothing
            if (page?.type === "page") return;
            createRoot(element).render(
                <div className="mobile-only mobile-quickform">
                    <SidebarQuickForm page={page} phoneForm />
                </div>
            );
        });
        bodyRef.current
        .querySelectorAll('[data-type="course-calendar"]')
        .forEach((element) => {
            createRoot(element).render(<CourseCalendar />);
        });
        bodyRef.current
        .querySelectorAll('[data-type="referral-registration"]')
        .forEach((element) => {
            // The referral pages' sign-up form, where the server put {{REGISTRATION}}'s mount point
            if (element.dataset.initialized) return;
            element.dataset.initialized = "true";
            createRoot(element).render(<ReferralRegistrationForm form={element.dataset.form} />);
        });
        bodyRef.current
        .querySelectorAll('[data-type="mobile-mentor-block"]')
        .forEach((element) => {
            // Shown under 631px, as the live theme's .mobile_mentor_block (Mentor.css)
            createRoot(element).render(
                <div className="mobile-mentor-block">
                    <Mentor page={page} sides />
                </div>
            );
        });

}, [page?.body]);
// Home page video: on the Drupal site a script swaps the cover image for the
// video when it is clicked. Scripts in the body are not run here, so do the same.
useEffect(() => {
    if (!bodyRef.current) return;

    const overlays = bodyRef.current.querySelectorAll('.video_overlay');
    const playVideo = (event) => {
        const overlay = event.currentTarget;
        const cover = overlay.querySelector('.cover_image');
        if (!cover) return;

        const video = document.createElement('video');
        // The video sits next to its cover image in the Drupal files folder
        video.src = new URL('home_page_video1.mp4', cover.src).href;
        video.controls = true;
        video.autoplay = true;
        overlay.parentNode.replaceChild(video, overlay);
    };

    overlays.forEach((overlay) => overlay.addEventListener('click', playVideo));
    return () => overlays.forEach((overlay) => overlay.removeEventListener('click', playVideo));
}, [page?.body]);
// YouTube players (<div class="video" data-id="...">), see youtubePlayers.js
useEffect(() => {
    if (!bodyRef.current) return;

    addYoutubePlayers(bodyRef.current);
}, [page?.body]);
// Audio players (<div class="audio-player-wrapper">): on the Drupal site each one is set up by
// its own script in the body, which names the picture and the recording. Scripts in the body
// are not run here, so the same is done for the scripts listed in AUDIO_PLAYERS.
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const wrapper = body.querySelector('.audio-player-wrapper');
    const script = [...body.querySelectorAll('script[src*="/js/audio/"]')]
        .map((el) => el.getAttribute('src').split('/').pop())
        .find((name) => AUDIO_PLAYERS[name]);
    if (!wrapper || !script || wrapper.querySelector('audio')) return;

    const song = AUDIO_PLAYERS[script];
    const audio = document.createElement('audio');
    audio.src = configData.DRUPAL_LIVE_DOMAIN_URL + song.audio;
    audio.preload = 'none';
    const image = document.createElement('img');
    image.src = configData.DRUPAL_LIVE_DOMAIN_URL + song.image;
    image.alt = '';
    const nameEl = wrapper.querySelector('.audio-player-song-name');
    const bodyName = nameEl?.innerHTML;
    if (nameEl && song.name) nameEl.innerHTML = song.name;
    wrapper.querySelector('.audio-player-image')?.appendChild(image);
    wrapper.appendChild(audio);

    const button = wrapper.querySelector('.audio-player-button');
    const progress = wrapper.querySelector('.audio-player-progress');
    const bar = wrapper.querySelector('.audio-player-progress-bar');
    const showState = () => {
        button?.classList.toggle('icon-play', audio.paused);
        button?.classList.toggle('icon-pause', !audio.paused);
    };
    const showProgress = () => {
        if (bar && audio.duration) bar.style.width = (audio.currentTime / audio.duration) * 100 + '%';
    };
    const togglePlay = () => (audio.paused ? audio.play() : audio.pause());
    const seek = (event) => {
        if (audio.duration) audio.currentTime = audio.duration * (event.offsetX / progress.offsetWidth);
    };

    audio.addEventListener('play', showState);
    audio.addEventListener('pause', showState);
    audio.addEventListener('timeupdate', showProgress);
    button?.addEventListener('click', togglePlay);
    progress?.addEventListener('click', seek);
    return () => {
        audio.pause();
        button?.removeEventListener('click', togglePlay);
        progress?.removeEventListener('click', seek);
        if (nameEl && song.name) nameEl.innerHTML = bodyName;
        image.remove();
        audio.remove();
    };
}, [page?.body]);
// Conference pages: the "Be The First To Know" buttons in the body open the sign-up popup
useEffect(() => {
    if (!bodyRef.current) return;

    const buttons = bodyRef.current.querySelectorAll('[data-type="be-the-first-to-know"]');
    const openPopup = (event) => {
        event.preventDefault();
        setShowBeTheFirstToKnow(true);
    };

    buttons.forEach((button) => button.addEventListener('click', openPopup));
    return () => buttons.forEach((button) => button.removeEventListener('click', openPopup));
}, [page?.body]);
// Collapsible sections (<div class="dp-accordion"><p class="morebar" data-target="#id">): the
// page's own inline script opened and closed them on the Drupal site (jQuery slideToggle); the
// page's own styles hide the sections and turn the arrow on ".open".
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const toggle = (event) => {
        const bar = event.target.closest('.dp-accordion > .morebar');
        if (!bar) return;
        event.preventDefault();
        const section = bar.dataset.target && body.querySelector(bar.dataset.target);
        const opening = bar.classList.toggle('open');
        if (section) slideSection(section, opening);
    };

    body.addEventListener('click', toggle);
    return () => body.removeEventListener('click', toggle);
}, [page?.body]);
// Older collapsible sections (<p class="morebar" id="morebar2"> and <div class="morebarinfo"
// id="morebar22">, on the London and Hockey conference pages): the page's own inline script
// pairs each bar with its section and may hide the sections first (London; Hockey hides them in
// its styles). Scripts in the body are not run here, so do what the script says. Its hiding is
// only done to those sections, and before the page is drawn, so they do not show for a moment.
useLayoutEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const script = [...body.querySelectorAll('script:not([src])')].map((el) => el.textContent).join('\n');
    const toggles = [...script.matchAll(CLICK_SLIDE_TOGGLE)].map((match) => ({ bar: match[2], section: match[4] }));
    if (!toggles.length) return;

    const isSection = (el) => toggles.some(({ section }) => matches(el, section));
    for (const [, , selector] of script.matchAll(HIDE)) {
        select(body, selector).filter(isSection).forEach((section) => { section.style.display = 'none'; });
    }

    // Each bar the click is on (as jQuery runs the handlers of the bar and the bars around it)
    // opens its sections if they are closed, else closes them
    const toggle = (event) => {
        if (!(event.target instanceof Element)) return;
        toggles
            .filter(({ bar }) => within(event.target, bar))
            .forEach(({ section: selector }) => select(body, selector).forEach((section) => {
                const shown = section.dataset.open ? section.dataset.open === 'true' : getComputedStyle(section).display !== 'none';
                slideSection(section, !shown);
            }));
    };

    body.addEventListener('click', toggle);
    return () => body.removeEventListener('click', toggle);
}, [page?.body]);
// DOC Talks page: the year tabs show the past talks of one year (the current one at first), and
// the talk cards' buttons open the registration and recording forms. Drupal's doctalk.js did this.
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const earlier = body.querySelector('#earlier-years');
    const showYear = (year) => {
        body.querySelectorAll('.past-talk-card').forEach((talk) => {
            talk.style.display = year === 'all' || talk.dataset.year === year ? '' : 'none';
        });
    };
    const activeTab = body.querySelector('.year-tab.active');
    if (activeTab) showYear(activeTab.dataset.year);

    const onClick = (event) => {
        const tab = event.target.closest('.year-tab');
        const register = event.target.closest('[data-doc-talk-register]');
        const recording = event.target.closest('[data-doc-talk-recording]');
        if (tab) {
            body.querySelectorAll('.year-tab').forEach((other) => other.classList.remove('active'));
            tab.classList.add('active');
            showYear(tab.dataset.year);
            if (earlier) earlier.value = '';
        } else if (register) {
            event.preventDefault();
            setDocTalkPopup({ mode: 'register', id: register.dataset.docTalkRegister });
        } else if (recording) {
            event.preventDefault();
            setDocTalkPopup({ mode: 'recording', id: recording.dataset.docTalkRecording, mentors: recording.dataset.mentors });
        }
    };
    const onEarlier = () => {
        body.querySelectorAll('.year-tab').forEach((tab) => tab.classList.remove('active'));
        showYear(earlier.value || 'all');
    };

    body.addEventListener('click', onClick);
    earlier?.addEventListener('change', onEarlier);
    return () => {
        body.removeEventListener('click', onClick);
        earlier?.removeEventListener('change', onEarlier);
    };
}, [page?.body]);
// Login.css gives every .form-submit its blue with !important, which the live theme does not: a
// button's own colour in the body (Contact Us: style="background-color: #FF0000") would lose to it
useEffect(() => {
    bodyRef.current?.querySelectorAll('.form-submit[style*="background-color"]').forEach((el) => {
        el.style.setProperty('background-color', el.style.backgroundColor, 'important');
    });
}, [page?.body]);
// The Talk With Dr. Lashbrook form (Drupal's lynn_form, in that page's body): the page's own script
// made it work on the Drupal site (TalkWithLynnForm.js)
useEffect(() => {
    const form = bodyRef.current?.querySelector('form#info-form');
    if (!form?.querySelector('input[name="form_id"][value="lynn_form"]')) return;
    return setUpTalkWithLynnForm(form);
}, [page?.body]);
// The lead form with the Advanced Certificate pages' classes (form.AC-form, on the Sports Career
// Strategies course): the page's own script filled in its state list and swapped it for the province
// box by country on the Drupal site (AdvancedCertificateLeadForm.js)
useEffect(() => {
    const form = bodyRef.current?.querySelector('form.AC-form');
    if (!form) return;
    return setUpAdvancedCertificateLeadForm(form);
}, [page?.body]);
// Body images that fail to load are hidden so a missing file does not leave an empty box. One
// typed into the page with the Drupal copy's address (betasmww) may just be missing there (added
// on the live site after the copy was taken), so it is tried on the live site once first; videos
// (<video><source src>) with such an address the same way.
useEffect(() => {
    if (!bodyRef.current) return;

    const body = bodyRef.current;
    const copyFiles = configData.DRUPAL_DOMAIN_URL + '/sites/';
    const liveFiles = configData.DRUPAL_LIVE_DOMAIN_URL + '/sites/';
    const retryOrHide = (img) => {
        if (img.src.startsWith(copyFiles)) {
            img.src = liveFiles + img.src.slice(copyFiles.length);
        } else {
            img.style.display = 'none';
        }
    };
    const retryVideo = (video) => {
        const sources = [video, ...video.querySelectorAll('source')].filter((el) => el.src.startsWith(copyFiles));
        if (!sources.length) return;
        sources.forEach((el) => { el.src = liveFiles + el.src.slice(copyFiles.length); });
        video.load();
    };
    const onError = (event) => {
        const el = event.target;
        if (el.tagName === 'IMG') retryOrHide(el);
        else if (el.tagName === 'VIDEO') retryVideo(el);
        else if (el.tagName === 'SOURCE' && el.parentElement?.tagName === 'VIDEO') retryVideo(el.parentElement);
    };

    // error events do not bubble, so listen in the capture phase
    body.addEventListener('error', onError, true);
    body.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth === 0) retryOrHide(img);
    });
    body.querySelectorAll('video').forEach((video) => {
        if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) retryVideo(video);
    });
    return () => body.removeEventListener('error', onError, true);
}, [page?.body]);
  // A course body that was saved from the page editor comes back with its Drupal PHP broken up:
  // the rest of the block at the top is left as text (stripLeftoverPhp), and the link the block
  // worked out is left as "&lt;?php echo $timezone_link; ?&gt;". Take the text out and put the
  // course's weekly chat link in, as the server does for an unbroken body. A body repaired since
  // has the placeholder [timezone_link] there instead (%5B...%5D if an editor encoded it), which
  // gets the same link.
  const timezoneLink = page?.course_chats?.[0]?.timezoneLink || "";
  // The "CALL TODAY" box under the payment details has the class "applytoday_callus_wrapper" on
  // the Drupal site, and a page's own styles can hide the box by it (the football agent
  // apprenticeship does). The box the server builds comes without the class, so add it.
  // Drupal files (/sites/...) are not on this server, so load them from the live Drupal site,
  // which has every file: pictures and scripts (src), and a page's own stylesheets and file
  // links (href: a conference's program PDF, recap photos). Not from the Drupal copy (betasmww),
  // which lacks the files added after it was taken and is not part of go-live.
  // The theme's font stylesheets this site has (THEME_FONT_STYLESHEET).
  // Links typed with the live domain (such as the SMWW Apprenticeships cards) would leave this
  // site, so keep them on the current one (keepLiveLinksOnSite)
  // Pages whose text format on the live site converts line breaks (LINE_BREAK_PAGES) get that first.
  // The referral pages' {{REGISTRATION}} is the sign-up form's mount point, which the server puts
  // there when the page's Form Switch asks for the form; a server without that yet sends the
  // placeholder itself, which gets the mount point here (ReferralRegistrationForm).
  const referralForm = REFERRAL_FORM_PAGES[Number(page?.nid)];
  const referralMount = referralForm ? `<div data-type="referral-registration" data-form="${referralForm}"></div>` : '';
  const storedBody = page?.body || "";
  const bodyHtml = keepLiveLinksOnSite(stripLeftoverPhp(LINE_BREAK_PAGES.has(Number(page?.nid)) ? drupalAutop(storedBody) : storedBody)
    .replace(/\{\{REGISTRATION\}\}/g, referralMount)
    .replace(/&lt;\?php\s+echo\s+\$timezone_link\w*\s*;?\s*\?&gt;|\[timezone_link\]|%5Btimezone_link%5D/gi, timezoneLink)
    .replace(/<div id="application_info"(?![^>]*\bclass=)(?=[^>]*>\s*<div[^>]*>\s*CALL TODAY)/g, '<div id="application_info" class="applytoday_callus_wrapper"')
    .replace(THEME_FONT_STYLESHEET, themeFontStylesheet)
    .replace(VIDEO_STYLESHEET, '$1' + process.env.PUBLIC_URL + '$2')
    .replace(/(\s(?:src|href)=["'])\/sites\//g, `$1${configData.DRUPAL_LIVE_DOMAIN_URL}/sites/`));
  return (
    <div className={'courses-overview-container' + (editorClasses ? ' drupal-editor-classes' : '')}>
      <div  ref={bodyRef}
                dangerouslySetInnerHTML={{
                    __html: bodyHtml
                }}
            />
      <BeTheFirstToKnowPopup open={showBeTheFirstToKnow} onClose={() => setShowBeTheFirstToKnow(false)} />
      <DocTalkPopup popup={docTalkPopup} onClose={() => setDocTalkPopup(null)} />
    </div>
  );
};
export default Body;